import { useState } from "react";
import { Send } from "lucide-react";
import { deleteContact, fetchContacts } from "../smsApi";
import { formatPhone } from "../phone";
import { useContacts } from "../useContacts";
import type { SmsContact, SmsContactListType } from "../types";
import { ContactModal } from "../components/ContactModal";
import { ComposeModal } from "../components/ComposeModal";
import { BlastComposeModal } from "../components/BlastComposeModal";

const LIST_FILTERS: ("All" | SmsContactListType)[] = [
  "All",
  "Employee",
  "Customer",
  "Member",
  "Other",
];

type Props = {
  isAdmin: boolean;
  refreshKey: number;
  onChange: () => void;
};

export function ContactsView({ isAdmin, refreshKey, onChange }: Props) {
  const { contacts, loading, error, reload } = useContacts(refreshKey);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"All" | SmsContactListType>("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<SmsContact | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeNumber, setComposeNumber] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [blastOpen, setBlastOpen] = useState(false);

  const filtered = contacts.filter((c) => {
    if (filter !== "All" && c.list_type !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.phone_number.includes(q) ||
        formatPhone(c.phone_number).includes(q)
      );
    }
    return true;
  });

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selected.has(c.id));

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((c) => next.delete(c.id));
      } else {
        filtered.forEach((c) => next.add(c.id));
      }
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function handleDelete(contact: SmsContact) {
    if (!confirm(`Delete ${contact.name}?`)) return;
    try {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(contact.id);
        return next;
      });
      await deleteContact(contact.id);
      onChange();
      reload();
    } catch (e) {
      console.error("Failed to delete contact", e);
      alert("Failed to delete contact: " + (e as Error).message);
      await fetchContacts().catch(() => {});
      reload();
    }
  }

  function handleEdit(contact: SmsContact) {
    if (!isAdmin) return;
    setEditingContact(contact);
    setModalOpen(true);
  }

  function handleAdd() {
    if (!isAdmin) return;
    setEditingContact(null);
    setModalOpen(true);
  }

  const selectedContacts = contacts.filter((c) => selected.has(c.id));

  return (
    <div className="flex h-full flex-col">
      <div className="flex-shrink-0 border-b border-tribal-border bg-tribal-surface px-4 py-3">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold text-white">
            Contacts
            <span className="ml-2 text-xs font-normal text-tribal-muted">
              {filtered.length}
            </span>
          </h2>
          {isAdmin && (
            <button
              type="button"
              onClick={handleAdd}
              className="rounded-lg bg-tribal-red px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark"
            >
              Add Contact
            </button>
          )}
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or number…"
          className="mb-3 w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
        />

        <div className="flex flex-wrap gap-1.5">
          {LIST_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                filter === f
                  ? "bg-tribal-red text-white"
                  : "bg-tribal-border text-tribal-muted hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-tribal-border bg-tribal-surface/80 px-4 py-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-white">
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-tribal-muted transition-colors hover:text-white"
            >
              Clear
            </button>
          </div>
          <button
            type="button"
            onClick={() => setBlastOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-tribal-red px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark"
          >
            <Send className="h-3.5 w-3.5" />
            Send to Selected
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-tribal-muted">
            <div className="animate-pulse">Loading contacts…</div>
          </div>
        ) : error ? (
          <div className="mx-4 mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
            <p className="font-medium">Failed to load contacts</p>
            <p className="mt-1 font-mono text-xs">{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-tribal-muted">
            {search || filter !== "All"
              ? "No contacts match your search"
              : "No contacts yet"}
          </div>
        ) : (
          <div className="divide-y divide-tribal-border">
            <div className="flex items-center gap-3 bg-tribal-surface/40 px-4 py-2">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
                className="h-4 w-4 cursor-pointer rounded border-tribal-border bg-tribal-bg text-tribal-red focus:ring-0 focus:ring-offset-0"
              />
              <span className="text-xs text-tribal-muted">
                Select all {filter !== "All" ? filter + "s" : "contacts"} ({filtered.length})
              </span>
            </div>

            {filtered.map((c) => {
              const isSelected = selected.has(c.id);
              return (
                <div
                  key={c.id}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    isSelected ? "bg-white/5" : "hover:bg-white/5"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(c.id)}
                    className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-tribal-border bg-tribal-bg text-tribal-red focus:ring-0 focus:ring-offset-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-white">
                        {c.name}
                      </span>
                      <span className="flex-shrink-0 rounded bg-tribal-border px-1.5 py-0.5 text-[10px] font-medium text-tribal-muted">
                        {c.list_type}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-tribal-muted">
                      {formatPhone(c.phone_number)}
                    </p>
                    {c.notes && (
                      <p className="mt-0.5 truncate text-xs text-tribal-muted">
                        {c.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setComposeNumber(c.phone_number);
                        setComposeOpen(true);
                      }}
                      className="rounded-lg bg-tribal-red px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-tribal-red-dark"
                    >
                      Message
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleEdit(c)}
                          className="rounded-lg bg-tribal-border px-2.5 py-1.5 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(c)}
                          className="rounded-lg bg-tribal-border px-2.5 py-1.5 text-xs text-red-400 transition-colors hover:bg-white/10 hover:text-red-300"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && (
        <ContactModal
          contact={
            editingContact ?? {
              id: "",
              name: "",
              phone_number: "",
              list_type: "Customer",
              notes: null,
              created_at: "",
            }
          }
          mode={editingContact ? "edit" : "create"}
          onClose={() => {
            setModalOpen(false);
            setEditingContact(null);
          }}
          onSaved={() => {
            onChange();
            reload();
          }}
        />
      )}

      {composeOpen && (
        <ComposeModal
          initialTo={composeNumber}
          onClose={() => {
            setComposeOpen(false);
            setComposeNumber("");
          }}
          onSent={() => {
            setComposeOpen(false);
            setComposeNumber("");
          }}
        />
      )}

      {blastOpen && selectedContacts.length > 0 && (
        <BlastComposeModal
          recipients={selectedContacts}
          onClose={() => setBlastOpen(false)}
          onSent={() => clearSelection()}
        />
      )}
    </div>
  );
}
